import styled from "@emotion/styled";
import { Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  BookingTypeEnum,
  DepotSynchronizationLogItemSynchronizationStatusEnum,
} from "@rentablo/api/apiv1";
import { toRoundedNumberOfLots, toCurrency } from "@helpers";
import { BookingType } from "@components";
import { Badge } from "@rentablo/components";

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(6)};
  width: 100%;
`;

const TypeWithNameContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  width: 100%;
  min-width: 0;
`;

const TypeWithName = ({ logItem }: { logItem: LogItem }) => {
  const { t } = useTranslation();
  const wasRolledBack = !logItem.type && logItem.bookingId;

  if (logItem.synchronizationStatus === "FAILED") {
    return (
      <TypeWithNameContainer>
        <Badge variant="error">{t("synchronizationLog.checkRequired")}</Badge>
        <Typography variant="body4" noWrap width="100%">
          {logItem.name}
        </Typography>
      </TypeWithNameContainer>
    );
  }

  return (
    <TypeWithNameContainer>
      <Badge
        variant={wasRolledBack ? "default" : "accent"}
        data-testid={`log-item-${wasRolledBack ? "rolled-back" : "new"}`}
      >
        {wasRolledBack
          ? t("synchronizationLog.rolledBack")
          : t("synchronizationLog.new")}
      </Badge>
      {wasRolledBack ? "" : <BookingType type={logItem.type} />}
      <Typography
        variant="body1"
        fontWeight={600}
        noWrap
        width="100%"
        minWidth={0}
      >
        {logItem.name}
      </Typography>
    </TypeWithNameContainer>
  );
};

export interface LogItem {
  id: number;
  synchronizationStatus: DepotSynchronizationLogItemSynchronizationStatusEnum;
  bookingId?: number;
  investmentId?: number;
  isin?: string;
  name?: string;
  numberOfLots?: number;
  amount?: number;
  currency?: string;
  type?: BookingTypeEnum;
}

export interface SynchronizationLogItemProps {
  logItem: LogItem;
}

export const SynchronizationLogItem = ({
  logItem,
}: SynchronizationLogItemProps) => {
  const wasRolledBack = !logItem.type && logItem.bookingId;

  return (
    <Container>
      <TypeWithName logItem={logItem} />
      {!wasRolledBack && logItem.synchronizationStatus === "SUCCESS" && (
        <>
          <Typography
            variant="body1"
            fontWeight={600}
            color="textSecondary"
            flexShrink={0}
          >
            {logItem.type === "dividend"
              ? toCurrency(logItem.amount, logItem.currency)
              : toRoundedNumberOfLots(logItem.numberOfLots!)}
          </Typography>
        </>
      )}
    </Container>
  );
};
